import type { Action, MiniAction } from '@otto-ec/tracking-bct';
import { getPageCluster } from '@shared/domain/PageCluster.ts';
import { log } from '@shared/log.browser.ts';
import tracking from '@shared/tracking/Tracking.browser';
import type { FrontendTrackingLabels, KnownTrackingLabels, TrackingLabels } from '@shared/tracking/TrackingLabels';
import type { NavigationHistory } from '@shared/types/History.ts';
import { type OnColorChangePayload, isSimilarProduct } from '@shared/types/types';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { ProductItem, TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import {
    featureIdFromLocalListPosition,
    featuresFromTileListItems,
} from '@tilelist/domain/tracking/FeatureTracking.ts';
import { getSelectionRulesTracking } from '@tilelist/services/tracking/SelectionRuleTracking.browser.ts';

type ColorChangeTrackingParams = OnColorChangePayload & { localListPosition: number | undefined };

export class TrackingService implements TrackingPort {
    private navigationHistory: NavigationHistory | null = null;
    private navigationHistoryEnabled: boolean = false;

    configureNavigationHistory(navigationHistory: NavigationHistory, enabled: boolean): void {
        this.navigationHistory = navigationHistory;
        this.navigationHistoryEnabled = enabled;
    }
    async getPageViewTrackingSfid(): Promise<string> {
        return tracking.getPageMergeId();
    }

    trackTileVisibility(item: TileItem): void {
        const featureId = featureIdFromLocalListPosition(item.localListPosition);
        const miniAction: MiniAction = { featureId, action: 'scroll', status: 'visible' };

        log.debug(`trackTileVisibility -- miniAction ${JSON.stringify(miniAction)}`);
        tracking.submitMiniActions([miniAction]);
    }

    trackColorChange(params: ColorChangeTrackingParams): void {
        const { variationId, amountColors, localListPosition } = params;

        if (amountColors && localListPosition) {
            const colorChangeTrackingData: Pick<TrackingLabels, 'san_Interaction'> = {
                san_Interaction: 'product_color_change',
            };
            const colorChangeFeatureTracking: Action = {
                name: 'change',
                features: [
                    {
                        id: 'find_tile_' + localListPosition,
                        name: 'tile',
                        status: 'changed',
                        position: +localListPosition,
                        variationId: variationId,
                        labels: {
                            san_ProductColorCount: [`${+amountColors}`],
                        },
                    },
                ],
            };
            log.debug(
                `trackColorChange ${JSON.stringify(colorChangeTrackingData)} -- action ${JSON.stringify(colorChangeFeatureTracking)}`,
            );
            tracking.submitAction(colorChangeTrackingData, colorChangeFeatureTracking);
        }
    }

    trackClick(event: Event, productItem: ProductItem, rule: string): void {
        const { currentTarget } = event;
        if (!(currentTarget instanceof HTMLElement)) return;

        const element = currentTarget;
        const linkType = element.dataset.linkType;
        const pageCluster = getPageCluster();
        if (!linkType || !pageCluster) return;

        const trackingData: TrackingLabels = productItem.product.clickTracking
            ? {
                  ...productItem.product.clickTracking,
                  ...getSelectionRulesTracking(rule, this.navigationHistory, this.navigationHistoryEnabled),
              }
            : getSelectionRulesTracking(rule, this.navigationHistory, this.navigationHistoryEnabled);

        if (isSimilarProduct(productItem.product)) {
            trackingData['wk.san_ListPositionSimilar'] = productItem.originPosition + 1;
        }

        trackingData.san_Interaction = `${productItem.product.productType}_product_tile`;
        trackingData['wk.san_ListPosition'] = productItem.actualListPosition;
        trackingData['wk.san_ProductType'] = productItem.product.productType;
        trackingData['wk.san_ProductClickListType'] = pageCluster;
        trackingData['wk.san_ProductLinkType'] = linkType;

        log.debug(`trackClick ${JSON.stringify(trackingData)}`);
        tracking.trackOnNextPageImpression(trackingData);
    }

    trackBrandTileClick(listPosition: number, rule: string): void {
        const pageCluster = getPageCluster();
        if (!pageCluster) return;

        const trackingData: TrackingLabels = {
            ...getSelectionRulesTracking(rule, this.navigationHistory, this.navigationHistoryEnabled),
            san_Interaction: 'brand_tile',
            san_ListPosition: listPosition,
            san_ProductType: 'brand',
        };

        log.debug(`trackBrandTileClick ${JSON.stringify(trackingData)}`);
        tracking.trackOnNextPageImpression(trackingData);
    }

    trackPageView(pageView: TileListPageLayout, frontendTracking: FrontendTrackingLabels): void {
        const baseTrackingLabels = {
            ...frontendTracking,
            ...pageView.tracking,
        };

        const features = featuresFromTileListItems(pageView.tileListItems);

        log.debug(`trackOnPageView ${JSON.stringify(baseTrackingLabels)} -- ${JSON.stringify(features)}`);
        tracking.addToPageImpression(baseTrackingLabels || {}, features || []);
    }

    trackPagination() {
        log.debug(`trackPagination -- san_Interaction: paging_bottom`);
        tracking.addToPageImpression({ san_Interaction: 'paging_bottom' });
    }

    trackUrl(url: string): void {
        log.debug(`trackUrl ${url}`);
        tracking.addToPageImpression({
            ot_VisibleUrl: decodeURI(url),
        });
    }

    trackPageCluster(pageCluster: string) {
        log.debug(`trackPageCluster ${pageCluster}`);
        tracking.addToPageImpression({ ot_PageCluster: pageCluster });
    }

    trackComparisonActivity(activity: NonNullable<KnownTrackingLabels['san_ComparisonActivity']>) {
        log.debug(`trackComparisonActivity -- activity: ${activity}`);
        tracking.submitEvent({ san_ComparisonActivity: activity });
    }

    trackInteraction(interaction: KnownTrackingLabels['san_Interaction']) {
        log.debug(`trackInteraction -- san_Interaction: ${interaction}`);
        tracking.submitEvent({ san_Interaction: interaction });
    }

    trackProductImageChange(imagePosition: number) {
        const flexLabel = `image_${imagePosition}`;
        log.debug(
            `trackProductImageChange -- san_Interaction: product_image_change -- san_ReptileExperimentFlexLabel: ${flexLabel}`,
        );
        tracking.submitEvent({
            san_Interaction: 'product_image_change',
            san_ReptileExperimentFlexLabel: flexLabel,
        });
    }

    trackListLayer(layer: NonNullable<KnownTrackingLabels['san_ListLayer']>) {
        log.debug(`trackListLayer -- san_ListLayer: ${layer}`);
        tracking.submitEvent({ san_ListLayer: layer });
    }
}
